import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import axios from 'axios';
import { ReportData } from '../types';

interface DataViewerProps {
  reportData: ReportData;
}

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://your-api-gateway-url.execute-api.us-east-2.amazonaws.com/prod';

const DataViewer: React.FC<DataViewerProps> = ({ reportData }) => {
  const { getIdTokenClaims } = useAuth0();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<any>({});
  const [showImage, setShowImage] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ show: boolean; message: string; severity: 'success' | 'error' }>({
    show: false,
    message: '',
    severity: 'success'
  });

  // Handle potential nested structure or direct structure
  let extractedData = reportData.extracted_data;

  // If extracted_data itself has a nested extracted_data property (double nesting issue)
  if (extractedData && typeof extractedData === 'object' && 'extracted_data' in extractedData) {
    console.warn('Double nested extracted_data detected, extracting inner data');
    extractedData = (extractedData as any).extracted_data;
  }

  // Initialize edited data when component mounts or when editing starts
  useEffect(() => {
    if (isEditing) {
      // Deep copy the extracted data for editing
      setEditedData(JSON.parse(JSON.stringify(extractedData)));
    }
  }, [isEditing, extractedData]);

  // Helper to extract value and confidence from either format
  const getFieldValue = (fieldData: any): { value: any; confidence?: number } => {
    // Handle DynamoDB Map wrapper first
    if (fieldData && typeof fieldData === 'object' && 'M' in fieldData) {
      fieldData = fieldData.M;
    }

    if (fieldData && typeof fieldData === 'object' && 'value' in fieldData && 'confidence' in fieldData) {
      // New v2_embedded format - need to handle DynamoDB format
      let value = fieldData.value;
      let confidence = fieldData.confidence;

      // Extract from DynamoDB format if needed
      if (value && typeof value === 'object') {
        if ('S' in value) value = value.S;
        else if ('N' in value) value = value.N;
        else if ('BOOL' in value) value = value.BOOL;
        else if ('M' in value) value = value.M;
        else if ('L' in value) {
          // For lists, we need to unwrap each item
          value = value.L.map((item: any) => {
            if (item && typeof item === 'object' && 'M' in item) {
              // This is a map item, extract its contents
              const unwrapped: any = {};
              for (const [key, val] of Object.entries(item.M)) {
                unwrapped[key] = getFieldValue(val).value;
              }
              return unwrapped;
            }
            // For other types, use getFieldValue recursively
            return getFieldValue(item).value;
          });
        }
      }

      // Extract confidence from DynamoDB format
      if (confidence && typeof confidence === 'object' && 'N' in confidence) {
        confidence = parseFloat(confidence.N);
      } else if (typeof confidence === 'object' && 'S' in confidence) {
        confidence = parseFloat(confidence.S);
      }

      return { value, confidence };
    } else {
      // Old v1_separate format or plain value - extract from DynamoDB format
      let value = fieldData;
      if (value && typeof value === 'object') {
        if ('S' in value) value = value.S;
        else if ('N' in value) value = value.N;
        else if ('BOOL' in value) value = value.BOOL;
        else if ('M' in value) value = value.M;
        else if ('L' in value) {
          // For lists, we need to unwrap each item
          value = value.L.map((item: any) => {
            if (item && typeof item === 'object' && 'M' in item) {
              // This is a map item, extract its contents
              const unwrapped: any = {};
              for (const [key, val] of Object.entries(item.M)) {
                unwrapped[key] = getFieldValue(val).value;
              }
              return unwrapped;
            }
            // For other types, use getFieldValue recursively
            return getFieldValue(item).value;
          });
        }
      }
      return { value, confidence: undefined };
    }
  };

  // Helper to update a field value in edited data
  const updateFieldValue = (path: string[], value: string) => {
    const newData = { ...editedData };
    let current = newData;

    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }

    const lastKey = path[path.length - 1];
    // If it's an embedded format field, update the value property
    if (current[lastKey] && typeof current[lastKey] === 'object' && 'value' in current[lastKey]) {
      current[lastKey] = { ...current[lastKey], value, confidence: 1.0 };
    } else {
      current[lastKey] = value;
    }

    setEditedData(newData);
  };

  // Get presigned URL for image viewing
  const fetchImageUrl = async () => {
    setLoading(true);
    try {
      // Get the S3 key from report data
      // Try multiple fields for compatibility with batch-processed documents
      const s3Key = reportData.s3_key ||
                    reportData.s3_original_key ||
                    reportData.s3_processed_key;

      if (!s3Key) {
        throw new Error('No S3 key found for this document');
      }

      // Get page number if it's a multi-page PDF
      const pageNumber = reportData.page_number || reportData.source_page_number;

      // Get Auth0 token
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Request presigned URL from API
      const response = await axios.get(`${API_ENDPOINT}/image-url/${encodeURIComponent(s3Key)}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.data && response.data.imageUrl) {
        setImageUrl(response.data.imageUrl);
        setShowImage(true);
      } else {
        throw new Error('No image URL returned from API');
      }
    } catch (error: any) {
      console.error('Error fetching image URL:', error);
      setSaveStatus({
        show: true,
        message: 'Failed to load image. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Save edited data to DynamoDB and track corrections
  const saveEditedData = async () => {
    setLoading(true);
    try {
      // First, identify what corrections were made
      const corrections: Array<{
        field_name: string;
        original_value: string;
        corrected_value: string;
        confidence_before: number;
      }> = [];
      const originalData: any = reportData.extracted_data || {};

      // Compare edited data with original data
      Object.keys(editedData).forEach(field => {
        const originalField = originalData[field];
        const originalFieldData = getFieldValue(originalField);
        const originalValue = originalFieldData.value;
        const editedValue = editedData[field];

        // Check if the value was changed
        if (originalValue !== editedValue && editedValue !== undefined) {
          corrections.push({
            field_name: field,
            original_value: originalValue || '',
            corrected_value: editedValue || '',
            confidence_before: originalFieldData.confidence || 0
          });
        }
      });

      // If corrections were made, track them
      if (corrections.length > 0) {
        try {
          // Get Auth0 token
          const tokenClaims = await getIdTokenClaims();
          const idToken = tokenClaims?.__raw;

          await axios.post(`${API_ENDPOINT}/corrections`, {
            report_id: reportData.report_id,
            corrections: corrections
          }, {
            headers: {
              'Authorization': `Bearer ${idToken}`
            }
          });
          console.log(`Tracked ${corrections.length} corrections`);
        } catch (correctionError) {
          // Log error but don't fail the save
          console.error('Failed to track corrections:', correctionError);
        }
      }

      // Prepare the update payload
      const updatePayload = {
        report_id: reportData.report_id,
        ingestion_timestamp: reportData.ingestion_timestamp,
        extracted_data: editedData,
        user_validated: true,
        validation_timestamp: new Date().toISOString()
      };

      // Get Auth0 token
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Send update request to API
      const response = await axios.put(`${API_ENDPOINT}/report/${reportData.report_id}`, updatePayload, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      // Update the local report data
      reportData.extracted_data = editedData;

      setIsEditing(false);
      setSaveStatus({
        show: true,
        message: corrections.length > 0
          ? `Data saved successfully! ${corrections.length} correction(s) tracked for learning.`
          : 'Data saved successfully!',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error saving data:', error);
      setSaveStatus({
        show: true,
        message: 'Failed to save data. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Render confidence chip
  const renderConfidenceChip = (confidence: number | undefined) => {
    if (confidence === undefined) return null;

    const color = confidence === 1.0 ? 'success' :
                  confidence > 0.7 ? 'primary' :
                  confidence > 0.5 ? 'warning' : 'error';

    return (
      <Chip
        label={`${(confidence * 100).toFixed(0)}%`}
        color={color}
        size="small"
        sx={{ ml: 1 }}
      />
    );
  };

  // Render editable field
  const renderEditableField = (label: string, path: string[], fieldData: any) => {
    const { value, confidence } = getFieldValue(fieldData);
    const isCurrentlyEditing = isEditing;

    if (isCurrentlyEditing) {
      // Get the current value from editedData
      let currentValue = editedData;
      for (const p of path) {
        currentValue = currentValue?.[p];
      }
      const editValue = getFieldValue(currentValue).value;

      return (
        <TextField
          fullWidth
          size="small"
          label={label}
          value={editValue || ''}
          onChange={(e) => updateFieldValue(path, e.target.value)}
          variant="outlined"
          sx={{ my: 0.5 }}
        />
      );
    }

    // Ensure value is a string, not an object
    let displayValue = value;
    if (typeof value === 'object' && value !== null) {
      console.error(`DEBUG renderEditableField: ${label} value is object:`, value);
      // If it's still an object, stringify it or extract the actual value
      if ('S' in value) displayValue = value.S;
      else if ('N' in value) displayValue = value.N;
      else if ('M' in value) {
        // This shouldn't happen - if we have an M here, getFieldValue didn't work properly
        console.error(`ERROR: ${label} still has M wrapper in renderEditableField:`, value.M);
        displayValue = '[Object - Error]';
      }
      else if (Array.isArray(value)) {
        // Handle arrays (like boolean_statements)
        if (value.length > 0 && typeof value[0] === 'object') {
          // Array of objects - stringify it for display
          displayValue = `[${value.length} items]`;
        } else {
          // Array of primitives - join them
          displayValue = value.join(', ');
        }
      }
      else if (typeof value === 'object') {
        // Check if it's the actual additionalFields object with field keys
        const keys = Object.keys(value);
        if (keys.includes('thermo_serial') || keys.includes('ambient_temp_f')) {
          console.error(`CRITICAL ERROR: Trying to render additionalFields object directly:`, value);
          displayValue = '[Fields Object - Error]';
        } else {
          displayValue = JSON.stringify(value);
        }
      }
    }

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>{label}</Typography>
        <Typography variant="body1">
          {displayValue || 'N/A'}
          {renderConfidenceChip(confidence)}
        </Typography>
      </Box>
    );
  };

  // Get confidence scores for backward compatibility
  const confidenceScores = reportData.confidence_scores || {};
  const dataVersion = reportData.data_version || 'v1_separate';

  // Basic Information Section
  const basicInfo = {
    test_id: getFieldValue(extractedData?.test_id),
    test_date: getFieldValue(extractedData?.test_date),
    test_time: getFieldValue(extractedData?.test_time),
    test_number: getFieldValue(extractedData?.test_number),
    station_name: getFieldValue(extractedData?.station_name),
    station_number: getFieldValue(extractedData?.station_number),
    device_serial: getFieldValue(extractedData?.device_serial),
    prover_serial: getFieldValue(extractedData?.prover_serial),
    customer: getFieldValue(extractedData?.customer),
    manifest_number: getFieldValue(extractedData?.manifest_number),
    product_name: getFieldValue(extractedData?.product_name),
    product_type: getFieldValue(extractedData?.product_type),
    test_type: getFieldValue(extractedData?.test_type),
    operator: getFieldValue(extractedData?.operator),
    witness: getFieldValue(extractedData?.witness),
  };

  // Test Runs
  const testRunsField = getFieldValue(extractedData?.test_runs);
  const testRuns = Array.isArray(testRunsField.value) ? testRunsField.value : [];

  // Averages - need to properly extract nested structure
  const averagesField = getFieldValue(extractedData?.averages);
  let averages = {};
  if (averagesField.value) {
    // Check if it's the nested structure with another 'value' layer
    if (averagesField.value.value && typeof averagesField.value.value === 'object') {
      averages = averagesField.value.value;
    } else if (typeof averagesField.value === 'object') {
      // Check if the value itself is a DynamoDB Map
      if ('M' in averagesField.value) {
        averages = averagesField.value.M;
      } else {
        averages = averagesField.value;
      }
    }
  }

  // Corrections - need to properly extract nested structure
  const correctionsField = getFieldValue(extractedData?.corrections);
  let corrections = {};
  if (correctionsField.value) {
    // Check if it's the nested structure with another 'value' layer
    if (correctionsField.value.value && typeof correctionsField.value.value === 'object') {
      corrections = correctionsField.value.value;
    } else if (typeof correctionsField.value === 'object') {
      // Check if the value itself is a DynamoDB Map
      if ('M' in correctionsField.value) {
        corrections = correctionsField.value.M;
      } else {
        corrections = correctionsField.value;
      }
    }
  }

  // Final Results - need to properly extract nested structure
  const finalResultsField = getFieldValue(extractedData?.final_results);
  let finalResults = {};
  if (finalResultsField.value) {
    // Check if it's the nested structure with another 'value' layer
    if (finalResultsField.value.value && typeof finalResultsField.value.value === 'object') {
      finalResults = finalResultsField.value.value;
    } else if (typeof finalResultsField.value === 'object') {
      // Check if the value itself is a DynamoDB Map
      if ('M' in finalResultsField.value) {
        finalResults = finalResultsField.value.M;
      } else {
        finalResults = finalResultsField.value;
      }
    }
  }

  // Additional Fields - need to properly extract nested structure
  const additionalFieldsField = getFieldValue(extractedData?.additional_fields);
  let additionalFields: { [key: string]: any } = {};

  // Debug logging
  console.error('DEBUG: Raw additional_fields:', extractedData?.additional_fields);
  console.error('DEBUG: After getFieldValue:', additionalFieldsField);

  if (additionalFieldsField.value) {
    // Check if it's the nested structure with another 'value' layer
    if (additionalFieldsField.value.value && typeof additionalFieldsField.value.value === 'object') {
      additionalFields = additionalFieldsField.value.value;
    } else if (typeof additionalFieldsField.value === 'object') {
      // Check if the value itself is a DynamoDB Map
      if ('M' in additionalFieldsField.value) {
        additionalFields = additionalFieldsField.value.M;
      } else {
        additionalFields = additionalFieldsField.value;
      }
    }
  } else if (typeof additionalFieldsField === 'object' && !additionalFieldsField.confidence) {
    // If getFieldValue returned the raw object (no value/confidence wrapper)
    if ('M' in additionalFieldsField) {
      additionalFields = (additionalFieldsField as any).M;
    } else {
      additionalFields = additionalFieldsField;
    }
  }

  console.error('DEBUG: Final additionalFields to render:', additionalFields);
  console.error('DEBUG: Keys in additionalFields:', Object.keys(additionalFields));
  if (Object.keys(additionalFields).length > 0) {
    const firstKey = Object.keys(additionalFields)[0];
    console.error(`DEBUG: First field (${firstKey}):`, additionalFields[firstKey]);
  }

  return (
    <>
      <Box sx={{ mt: 2 }}>
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} /> : <ImageIcon />}
            onClick={fetchImageUrl}
            disabled={loading}
          >
            Show Image
          </Button>

          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              color="secondary"
            >
              Edit Data
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={saveEditedData}
                disabled={loading}
                color="success"
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setIsEditing(false);
                  setEditedData({});
                }}
                disabled={loading}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>

        {/* KEY FIELDS FOR METER HISTORY - Most Important Section */}
        <Card
          sx={{
            mb: 3,
            borderColor: 'primary.main',
            borderWidth: 2,
            borderStyle: 'solid',
            backgroundColor: 'primary.50'
          }}
          elevation={3}
        >
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
              🔑 Key Fields for Meter History
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These fields are critical for meter tracking and control charts. Please verify and correct them carefully.
              {/* Detect document format */}
              {(() => {
                // report_format is the current AI-classified tag (SGS | PMEC | DEFAULT);
                // fall back to legacy `format`. flow_rate_averages is top-level now.
                const reportFormat = (
                  getFieldValue((extractedData as any)?.report_format).value ||
                  getFieldValue((extractedData as any)?.format).value || ''
                ).toString().toUpperCase();
                const topFlow = getFieldValue((extractedData as any)?.flow_rate_averages).value;
                const hasFlowRateAverages = (Array.isArray(topFlow) && topFlow.length > 0) ||
                  (extractedData?.averages as any)?.flow_rate_averages ||
                  ((extractedData?.averages as any)?.value?.flow_rate_averages);
                const hasAvgMeterFactor = (extractedData?.averages as any)?.avg_meter_factor ||
                  ((extractedData?.averages as any)?.value?.avg_meter_factor);

                if (reportFormat === 'SGS' || hasFlowRateAverages) {
                  return <Chip label="SGS Multi-Flow Format Detected" size="small" color="info" sx={{ ml: 2 }} />;
                } else if (reportFormat === 'PMEC' || hasAvgMeterFactor) {
                  return <Chip label="PMEC Format Detected" size="small" color="info" sx={{ ml: 2 }} />;
                } else {
                  return <Chip label="Standard Format" size="small" color="default" sx={{ ml: 2 }} />;
                }
              })()}
            </Typography>

            <Grid container spacing={3}>
              {/* Meter ID */}
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>METER ID</strong> *
                  </Typography>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={editedData?.meter_id?.value || editedData?.meter_id || ''}
                      onChange={(e) => updateFieldValue(['meter_id'], e.target.value.toUpperCase())}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontWeight: 'bold',
                          fontSize: '1.1rem'
                        }
                      }}
                    />
                  ) : (
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {getFieldValue(extractedData?.meter_id).value || 'Missing'}
                      {getFieldValue(extractedData?.meter_id).confidence !== undefined &&
                        renderConfidenceChip(getFieldValue(extractedData?.meter_id).confidence)}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Product Type */}
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>PRODUCT TYPE</strong> *
                  </Typography>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={editedData?.product_type?.value || editedData?.product_type || ''}
                      onChange={(e) => updateFieldValue(['product_type'], e.target.value.toUpperCase())}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontWeight: 'bold',
                          fontSize: '1.1rem'
                        }
                      }}
                    />
                  ) : (
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {getFieldValue(extractedData?.product_type).value || 'Missing'}
                      {getFieldValue(extractedData?.product_type).confidence !== undefined &&
                        renderConfidenceChip(getFieldValue(extractedData?.product_type).confidence)}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Meter Factor - Show correct field based on format */}
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>METER FACTOR</strong> *
                    {(() => {
                      // Check if we should use avg_meter_factor from averages
                      const avgMeterFactor = getFieldValue((extractedData?.averages as any)?.avg_meter_factor ||
                        (extractedData?.averages as any)?.value?.avg_meter_factor);

                      if (avgMeterFactor.value && avgMeterFactor.value !== getFieldValue(extractedData?.meter_factor).value) {
                        return <Chip label="Using Avg" size="small" sx={{ ml: 1 }} />;
                      }
                      return null;
                    })()}
                  </Typography>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={(() => {
                        // For editing, check multiple possible sources
                        if (editedData?.averages?.avg_meter_factor?.value) return editedData.averages.avg_meter_factor.value;
                        if (editedData?.averages?.value?.avg_meter_factor?.value) return editedData.averages.value.avg_meter_factor.value;
                        if (editedData?.meter_factor?.value) return editedData.meter_factor.value;
                        return editedData?.meter_factor || '';
                      })()}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Update both locations to ensure it's saved correctly
                        updateFieldValue(['meter_factor'], value);
                        // Also update avg_meter_factor if it exists
                        if (editedData?.averages?.avg_meter_factor !== undefined) {
                          updateFieldValue(['averages', 'avg_meter_factor'], value);
                        }
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontWeight: 'bold',
                          fontSize: '1.1rem'
                        }
                      }}
                    />
                  ) : (
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {(() => {
                        // Display logic: prefer avg_meter_factor from averages if it exists
                        const avgMF = getFieldValue((extractedData?.averages as any)?.avg_meter_factor ||
                          (extractedData?.averages as any)?.value?.avg_meter_factor);
                        const standardMF = getFieldValue(extractedData?.meter_factor);

                        const displayValue = avgMF.value || standardMF.value || 'Missing';
                        const confidence = avgMF.confidence !== undefined ? avgMF.confidence : standardMF.confidence;

                        return (
                          <>
                            {displayValue}
                            {confidence !== undefined && renderConfidenceChip(confidence)}
                          </>
                        );
                      })()}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Test Date */}
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>TEST DATE</strong> *
                  </Typography>
                  {isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      type="date"
                      value={editedData?.test_date?.value || editedData?.test_date || ''}
                      onChange={(e) => updateFieldValue(['test_date'], e.target.value)}
                      sx={{
                        '& .MuiInputBase-input': {
                          fontWeight: 'bold',
                          fontSize: '1.1rem'
                        }
                      }}
                    />
                  ) : (
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {getFieldValue(extractedData?.test_date).value || 'Missing'}
                      {getFieldValue(extractedData?.test_date).confidence !== undefined &&
                        renderConfidenceChip(getFieldValue(extractedData?.test_date).confidence)}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Flow Rate (optional but important) - Show for single flow reports */}
              {(extractedData?.flow_rate || isEditing) && (
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>FLOW RATE</strong> (optional)
                    </Typography>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={editedData?.flow_rate?.value || editedData?.flow_rate || ''}
                        onChange={(e) => updateFieldValue(['flow_rate'], e.target.value)}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontWeight: 'bold',
                            fontSize: '1.1rem'
                          }
                        }}
                      />
                    ) : (
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {getFieldValue(extractedData?.flow_rate).value || 'N/A'}
                        {getFieldValue(extractedData?.flow_rate).confidence !== undefined &&
                          renderConfidenceChip(getFieldValue(extractedData?.flow_rate).confidence)}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* SGS Multi-Flow Section - Show flow rate averages if present */}
            {(() => {
              const flowRateAverages = (extractedData?.averages as any)?.flow_rate_averages ||
                (extractedData?.averages as any)?.value?.flow_rate_averages;

              if (flowRateAverages && Array.isArray(flowRateAverages)) {
                return (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'info.main' }}>
                      🔄 SGS Multi-Flow Rate Averages (Each creates a separate control chart)
                    </Typography>
                    <Grid container spacing={2}>
                      {flowRateAverages.map((flow: any, index: number) => {
                        const flowRate = getFieldValue(flow.flow_rate || flow.M?.flow_rate);
                        const avgMeterFactor = getFieldValue(flow.average_meter_factor || flow.M?.average_meter_factor);
                        const repeatability = getFieldValue(flow.repeatability || flow.M?.repeatability);

                        return (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Box sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="body2" color="text.secondary">
                                Flow Rate {index + 1}
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {flowRate.value || 'N/A'} BPH
                              </Typography>
                              <Typography variant="body2">
                                MF: {avgMeterFactor.value || 'N/A'}
                              </Typography>
                              {repeatability.value && (
                                <Typography variant="body2">
                                  Rep: {repeatability.value}%
                                </Typography>
                              )}
                              {isEditing && (
                                <Box sx={{ mt: 1 }}>
                                  <TextField
                                    size="small"
                                    label="Avg MF"
                                    fullWidth
                                    value={editedData?.averages?.flow_rate_averages?.[index]?.average_meter_factor?.value || ''}
                                    onChange={(e) => updateFieldValue(
                                      ['averages', 'flow_rate_averages', index.toString(), 'average_meter_factor'],
                                      e.target.value
                                    )}
                                  />
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Note: Each flow rate creates a separate meter-product entry for tracking
                    </Typography>
                  </Box>
                );
              }
              return null;
            })()}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              * Required fields for meter history tracking. The system automatically detects SGS multi-flow and PMEC formats and uses the appropriate fields.
            </Typography>
          </CardContent>
        </Card>

        {/* Document Metadata */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Document Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Uploaded By
                  </Typography>
                  <Typography variant="body1">
                    {reportData.uploaded_by || 'Unknown'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Upload Date
                  </Typography>
                  <Typography variant="body1">
                    {reportData.ingestion_timestamp ?
                      new Date(reportData.ingestion_timestamp).toLocaleString() :
                      'Unknown'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Processing Status
                  </Typography>
                  <Chip
                    label={reportData.user_validated ? 'User Validated' : 'AI Processed'}
                    color={reportData.user_validated ? 'success' : 'primary'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Report ID
                  </Typography>
                  <Typography variant="body1">
                    {reportData.report_id}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Overall Confidence
                  </Typography>
                  <Typography variant="body1">
                    {reportData.overall_confidence ?
                      `${(reportData.overall_confidence * 100).toFixed(0)}%` :
                      'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Document Type
                  </Typography>
                  <Typography variant="body1">
                    {reportData.s3_key?.split('.').pop()?.toUpperCase() || 'Unknown'}
                  </Typography>
                </Box>
              </Grid>
              {(reportData.page_number || reportData.source_page_number) && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Page Information
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon fontSize="small" color="action" />
                      <Typography variant="body1">
                        Page {reportData.page_number || reportData.source_page_number}
                        {reportData.total_pages && ` of ${reportData.total_pages}`}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
              {reportData.original_file && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Original File
                    </Typography>
                    <Typography variant="body1" noWrap title={reportData.original_file}>
                      {reportData.original_file.split('/').pop()}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Basic Information */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Basic Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {Object.entries(basicInfo).map(([key, field]) => {
                const label = key.split('_').map(word =>
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ');

                // Extract the actual value from the field (which may be in DynamoDB format)
                const fieldValue = getFieldValue((extractedData as any)?.[key]);

                // Handle nested objects that would cause React error
                let displayValue = fieldValue.value;
                if (typeof displayValue === 'object' && displayValue !== null && !Array.isArray(displayValue)) {
                  if (displayValue.value !== undefined) {
                    displayValue = displayValue.value;
                  } else if (displayValue.M && displayValue.M.value && displayValue.M.value.S) {
                    displayValue = displayValue.M.value.S;
                  } else {
                    displayValue = JSON.stringify(displayValue);
                  }
                }

                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    {renderEditableField(label, [key], displayValue)}
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Test Runs */}
        {testRuns.length > 0 && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Test Runs
                {testRunsField.confidence !== undefined && renderConfidenceChip(testRunsField.confidence)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Run #</TableCell>
                      <TableCell>Pulses Run</TableCell>
                      <TableCell>Pulses Returned</TableCell>
                      <TableCell>Prover Temp</TableCell>
                      <TableCell>Meter Temp</TableCell>
                      <TableCell>Prover Pressure</TableCell>
                      <TableCell>Meter Pressure</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testRuns.map((run: any, index: number) => {
                      const runData = {
                        run_number: getFieldValue(run.run_number),
                        pulses_run: getFieldValue(run.pulses_run),
                        pulses_returned: getFieldValue(run.pulses_returned),
                        prover_temperature: getFieldValue(run.prover_temperature),
                        meter_temperature: getFieldValue(run.meter_temperature),
                        prover_pressure: getFieldValue(run.prover_pressure),
                        meter_pressure: getFieldValue(run.meter_pressure),
                      };

                      if (isEditing) {
                        return (
                          <TableRow key={index}>
                            {Object.entries(runData).map(([field, data]) => (
                              <TableCell key={field}>
                                <TextField
                                  size="small"
                                  value={
                                    editedData?.test_runs?.value?.[index]?.[field]?.value ||
                                    editedData?.test_runs?.[index]?.[field] ||
                                    ''
                                  }
                                  onChange={(e) => updateFieldValue(['test_runs', 'value', index.toString(), field], e.target.value)}
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      }

                      return (
                        <TableRow key={index}>
                          {Object.entries(runData).map(([field, data]) => (
                            <TableCell key={field}>
                              {data.value || 'N/A'}
                              {data.confidence !== undefined && renderConfidenceChip(data.confidence)}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Averages */}
        {Object.keys(averages).length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Averages
                {averagesField.confidence !== undefined && renderConfidenceChip(averagesField.confidence)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {Object.entries(averages).map(([key, field]) => {
                  const label = key.split('_').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ');

                  // Extract the actual value from the field (which may have M wrapper)
                  const fieldValue = getFieldValue(field);

                  // Special handling for flow_rate_averages array (SGS format)
                  if (key === 'flow_rate_averages' && Array.isArray(fieldValue.value)) {
                    return (
                      <Grid item xs={12} key={key}>
                        <Typography variant="subtitle2" gutterBottom>{label}</Typography>
                        <Grid container spacing={2}>
                          {fieldValue.value.map((flowData: any, index: number) => {
                            // Extract values from nested structure if needed
                            const getFlowValue = (field: any) => {
                              if (typeof field === 'object' && field !== null) {
                                if (field.value !== undefined) {
                                  return field.value;
                                }
                                // Handle M wrapper from DynamoDB
                                if (field.M && field.M.value && field.M.value.S) {
                                  return field.M.value.S;
                                }
                              }
                              return field;
                            };

                            // Extract all values first to avoid rendering objects
                            const origCorrection = getFlowValue(flowData.orig_correction);
                            const finalCorrection = getFlowValue(flowData.final_correction);
                            const preCalibPercent = getFlowValue(flowData.pre_calib_percent);
                            const postCalibPercent = getFlowValue(flowData.post_calib_percent);

                            return (
                              <Grid item xs={12} md={6} key={index}>
                                <Card variant="outlined">
                                  <CardContent>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                      Flow Rate: {getFlowValue(flowData.flow_rate) || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      Ave-Met-Factor: {getFlowValue(flowData.average_meter_factor) || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      Repeatability: {getFlowValue(flowData.repeatability) || 'N/A'}
                                    </Typography>
                                    {origCorrection && (
                                      <Typography variant="body2">
                                        Original Correction: {origCorrection}
                                      </Typography>
                                    )}
                                    {finalCorrection && (
                                      <Typography variant="body2">
                                        Final Correction: {finalCorrection}
                                      </Typography>
                                    )}
                                    {preCalibPercent && (
                                      <Typography variant="body2">
                                        Pre-Calib %: {preCalibPercent}
                                      </Typography>
                                    )}
                                    {postCalibPercent && (
                                      <Typography variant="body2">
                                        Post-Calib %: {postCalibPercent}
                                      </Typography>
                                    )}
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Grid>
                    );
                  }

                  // Regular field rendering - handle nested objects
                  // Check if fieldValue.value is an object that would cause React error
                  let displayValue = fieldValue.value;
                  if (typeof displayValue === 'object' && displayValue !== null && !Array.isArray(displayValue)) {
                    // This is a nested object - extract its value if it has one
                    if (displayValue.value !== undefined) {
                      displayValue = displayValue.value;
                    } else if (displayValue.M && displayValue.M.value && displayValue.M.value.S) {
                      displayValue = displayValue.M.value.S;
                    } else {
                      // Convert object to string representation
                      displayValue = JSON.stringify(displayValue);
                    }
                  }

                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      {renderEditableField(label, ['averages', 'value', key], displayValue)}
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Corrections */}
        {Object.keys(corrections).length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Correction Factors
                {correctionsField.confidence !== undefined && renderConfidenceChip(correctionsField.confidence)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {Object.entries(corrections).map(([key, field]) => {
                  const label = key.toUpperCase().replace(/_/g, ' ');

                  // Extract the actual value from the field (which may have M wrapper)
                  const fieldValue = getFieldValue(field);

                  // Handle nested objects
                  let displayValue = fieldValue.value;
                  if (typeof displayValue === 'object' && displayValue !== null && !Array.isArray(displayValue)) {
                    if (displayValue.value !== undefined) {
                      displayValue = displayValue.value;
                    } else if (displayValue.M && displayValue.M.value && displayValue.M.value.S) {
                      displayValue = displayValue.M.value.S;
                    } else {
                      displayValue = JSON.stringify(displayValue);
                    }
                  }

                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      {renderEditableField(label, ['corrections', 'value', key], displayValue)}
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Final Results */}
        {Object.keys(finalResults).length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Final Results
                {finalResultsField.confidence !== undefined && renderConfidenceChip(finalResultsField.confidence)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {Object.entries(finalResults).map(([key, field]) => {
                  const label = key.split('_').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ');

                  // Extract the actual value from the field (which may have M wrapper)
                  const fieldValue = getFieldValue(field);

                  // Handle nested objects
                  let displayValue = fieldValue.value;
                  if (typeof displayValue === 'object' && displayValue !== null && !Array.isArray(displayValue)) {
                    if (displayValue.value !== undefined) {
                      displayValue = displayValue.value;
                    } else if (displayValue.M && displayValue.M.value && displayValue.M.value.S) {
                      displayValue = displayValue.M.value.S;
                    } else {
                      displayValue = JSON.stringify(displayValue);
                    }
                  }

                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      {renderEditableField(label, ['final_results', 'value', key], displayValue)}
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Additional Fields */}
        {Object.keys(additionalFields).length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Additional Information
                {additionalFieldsField.confidence !== undefined && renderConfidenceChip(additionalFieldsField.confidence)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {Object.entries(additionalFields).map(([key, field]) => {
                  const label = key.split('_').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ');

                  // Extract the actual value from the field (which may have M wrapper)
                  const fieldValue = getFieldValue(field);

                  // Handle nested objects
                  let displayValue = fieldValue.value;
                  if (typeof displayValue === 'object' && displayValue !== null && !Array.isArray(displayValue)) {
                    if (displayValue.value !== undefined) {
                      displayValue = displayValue.value;
                    } else if (displayValue.M && displayValue.M.value && displayValue.M.value.S) {
                      displayValue = displayValue.M.value.S;
                    } else {
                      displayValue = JSON.stringify(displayValue);
                    }
                  }

                  return (
                    <Grid item xs={12} sm={6} md={4} key={key}>
                      {renderEditableField(label, ['additional_fields', 'value', key], displayValue)}
                    </Grid>
                  );
                })}
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Comments/Notes */}
        {extractedData?.comments && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              {renderEditableField('Comments/Notes', ['comments'], getFieldValue(extractedData.comments).value)}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Image Dialog */}
      <Dialog
        open={showImage}
        onClose={() => setShowImage(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Source Document
          {(reportData.page_number || reportData.source_page_number) && (
            <Chip
              label={`Page ${reportData.page_number || reportData.source_page_number}${reportData.total_pages ? ` of ${reportData.total_pages}` : ''}`}
              size="small"
              color="primary"
              sx={{ ml: 2 }}
            />
          )}
          <IconButton
            aria-label="close"
            onClick={() => setShowImage(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : imageUrl ? (
            <Box sx={{ p: 2 }}>
              {/* Check if it's a PDF or image based on S3 key */}
              {reportData.s3_key && (reportData.s3_key.toLowerCase().endsWith('.pdf') ||
                                     reportData.s3_key.toLowerCase().endsWith('.xlsx') ||
                                     reportData.s3_key.toLowerCase().endsWith('.xls')) ? (
                // For PDFs and Excel files, show an embed or link
                <Box>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Document type: {reportData.s3_key.split('.').pop()?.toUpperCase()}
                  </Typography>
                  {reportData.s3_key.toLowerCase().endsWith('.pdf') ? (
                    <Box>
                      <object
                        data={imageUrl}
                        type="application/pdf"
                        width="100%"
                        height="600px"
                        style={{
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      >
                        <embed
                          src={imageUrl}
                          type="application/pdf"
                          width="100%"
                          height="600px"
                        />
                        <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                          PDF preview unavailable.
                          <Button
                            variant="contained"
                            onClick={() => window.open(imageUrl, '_blank')}
                            sx={{ ml: 2 }}
                          >
                            Open PDF in New Tab
                          </Button>
                        </Typography>
                      </object>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Excel files cannot be displayed inline.
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={() => window.open(imageUrl, '_blank')}
                        sx={{ mt: 1 }}
                      >
                        Download/View in New Tab
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                // For images, show the img tag
                <Box sx={{ textAlign: 'center' }}>
                  <img
                    src={imageUrl}
                    alt="Source Document"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                    onError={(e) => {
                      console.error('Image failed to load:', e);
                      setSaveStatus({
                        show: true,
                        message: 'Image failed to load. Opening in new tab instead.',
                        severity: 'error'
                      });
                      window.open(imageUrl, '_blank');
                    }}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No document available
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Save Status Snackbar */}
      <Snackbar
        open={saveStatus.show}
        autoHideDuration={6000}
        onClose={() => setSaveStatus({ ...saveStatus, show: false })}
      >
        <Alert
          onClose={() => setSaveStatus({ ...saveStatus, show: false })}
          severity={saveStatus.severity}
        >
          {saveStatus.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DataViewer;